import { ApiError } from './api'
import { explain } from './staffErrors'
import { t, type StaffKey, type StaffLanguage } from './staffText'
import type { Identity } from './types'

// What the app does when a session ends while a screen is open.
//
// It asks "who am I" once before showing the password form. For somebody who
// signs in with a password that question answers 401 — /auth/me never announces
// a signed-out session itself — and the outcome is what it always was: the form,
// with a sentence saying why. Behind a proxy that signs people in, the same GET
// opens a new session on the proxy's word, and the person carries on without
// seeing a form they have no password for.

export interface SessionEndedDetail {
  code: string
  message: string
}

/**
 * What the sign-in screen should say, kept as a source rather than a resolved
 * sentence.
 *
 * The screen this notice appears on carries the three-language row right below
 * it (SignIn's Step 7), so the reader can switch language after the notice is
 * already showing. A sentence baked into state at the moment the notice was set
 * would be left in whatever language was current then — see noticeText, which
 * resolves one of these at render instead. `key` covers the two generic
 * sentences this file produces itself; `err` is the server's own refusal,
 * resolved through explain() so a proxy or directory refusal keeps its args and
 * its fallback to the server's English.
 */
export type Notice = { key: StaffKey } | { err: unknown } | null

export interface AfterSessionEnded {
  identity: Identity | null
  notice: Notice
  /** Anything but a plain "nobody is signed in", for the console. */
  error?: unknown
}

// refusalOf is the source of the sentence the server gave for not signing
// somebody in, or null for a plain "nobody is signed in". Behind a proxy,
// /auth/me is the sign-in, and its refusals — no account, no mapped group,
// disabled, locked — are written for the person the proxy named. Dropping them
// leaves that person at a password form they have no password for, with no idea
// why.
export function refusalOf(err: unknown): Notice {
  return err instanceof ApiError && err.code !== 'UNAUTHENTICATED' ? { err } : null
}

/** The generic notice for a session that ended, as a source — see Notice — not
 *  the event's own message: the server's detail is English only, and the
 *  reader may not be. */
export function endedNotice(detail: SessionEndedDetail | undefined): Notice {
  return { key: detail?.code === 'SESSION_IDLE' ? 'errors.SESSION_IDLE' : 'entry.sessionEnded' }
}

export async function afterSessionEnded(
  detail: SessionEndedDetail | undefined,
  me: () => Promise<Identity>,
  isUnauthenticated: (err: unknown) => boolean,
): Promise<AfterSessionEnded> {
  try {
    return { identity: await me(), notice: null }
  } catch (err) {
    return {
      identity: null,
      notice: refusalOf(err) ?? endedNotice(detail),
      error: isUnauthenticated(err) ? undefined : err,
    }
  }
}

/**
 * Resolve a Notice to text in the reader's language, at render rather than at
 * the moment it was set — so the same stored notice reads correctly whichever
 * language the screen is switched to afterwards.
 */
export function noticeText(notice: Notice, lang: StaffLanguage): string {
  if (!notice) return ''
  return 'key' in notice ? t(notice.key, lang) : explain(notice.err, lang, 'common.unreachable')
}

/**
 * A live socket cannot see a 401, so a staff page whose session ended would retry
 * it forever. This asks once; only a 401 announces the sign-out, and a server that
 * is down or restarting signs nobody out.
 */
export async function announceIfSignedOut(me: () => Promise<unknown>, announce: (detail: SessionEndedDetail) => void): Promise<void> {
  try {
    await me()
  } catch (err) {
    if (err instanceof ApiError && err.isUnauthenticated) announce({ code: err.code, message: err.message })
  }
}
